import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import EditPictureDialog from './EditPictureDialog';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { formatDate } from 'shared/utils/utils';
import { useParams, useLocation, useSearchParams } from "react-router-dom";
import useUserService from 'shared/api/services/userService';
import Header from "shared/components/Header";
import SearchableSelect from 'shared/components/SearchableSelect';
import ClockLoader from "shared/components/ClockLoader";
import { useTheme } from '@mui/material/styles';
import { useAuth } from 'shared/hooks/useAuth';
import { useTenantNavigate } from 'shared/hooks/useTenantNavigate';
import {
  getRegistryTenantIdFromSession,
  getSessionProfile,
} from 'shared/utils/sessionProfileStore';
import { getRegistryTenantIdFromSearch } from 'shared/utils/tenantRoute';
import {
  getTenantUserById,
  updateTenantUserRecord,
} from 'shared/api/services/tenantUserService';
import { toast } from 'react-hot-toast';

const ROLE_OPTIONS = ['USER', 'SERVICEADMIN', 'MANAGER', 'ADMIN', 'SYSTEMADMIN'];
const BASIC_ROLE_OPTIONS = ['USER', 'SERVICEADMIN'];
const ADMIN_RESET_PASSWORD_ROLES = new Set(['ADMIN', 'SERVICEADMIN']);

const roleAsString = (role) =>
  typeof role === 'string' ? role.trim().toUpperCase() : role?.name?.trim?.().toUpperCase?.() ?? '';

const normalizeRoleName = (role) =>
  roleAsString(role).replace(/[\s_-]+/g, '');
const statusAsString = (status) =>
  typeof status === 'string' ? status.trim().toUpperCase() : status?.name?.trim?.().toUpperCase?.() ?? 'ACTIVE';

const UserDetails = () => {
  const theme = useTheme();
  const { user: authUser } = useAuth();
  const { navigateTenant } = useTenantNavigate();
  const { userId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [userData, setUserData] = useState(null);
  const [changeImageDialogOpen, setChangeImageDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [activateAccountDialogOpen, setActivateAccountDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [activatingAccount, setActivatingAccount] = useState(false);
  const userService = useUserService();
  const { isLoading } = userService;
  const sessionProfile = authUser?.data ?? getSessionProfile();
  const registryTenantId =
    getRegistryTenantIdFromSearch(searchParams) ||
    (location.state?.registryTenantId != null
      ? String(location.state.registryTenantId).trim()
      : '') ||
    getRegistryTenantIdFromSession() ||
    '';
  const effectiveRegistryTenantId =
    registryTenantId !== '' ? registryTenantId : null;
  const currentUserRole = normalizeRoleName(sessionProfile?.role);
  const currentUserId = sessionProfile?.id != null ? String(sessionProfile.id) : '';
  const visibleRoleOptions =
    currentUserRole === 'ADMIN' || currentUserRole === 'SYSTEMADMIN'
      ? ROLE_OPTIONS
      : BASIC_ROLE_OPTIONS;
  const canAdminResetPassword =
    userId
    && String(userId) !== currentUserId
    && ADMIN_RESET_PASSWORD_ROLES.has(currentUserRole);
  const userStatus = statusAsString(userData?.status);
  const canActivateAccount = canAdminResetPassword && userStatus !== 'ACTIVE';
  const canResetPassword = canAdminResetPassword && userStatus === 'ACTIVE';

  const handleChange = (event) => {
    setUserData({ ...userData, [event.target.name]: event.target.value });
  };

  const handleDateChange = (newValue) => {
    setUserData((prevData) => ({
      ...prevData,
      date_Of_Birth: newValue,
    }));
  };

  useEffect(() => {
    if (userId) {
      loadUserDetails();
    }
  }, [userId, effectiveRegistryTenantId]);

  const loadUserDetails = async () => {
    try {
      const data = effectiveRegistryTenantId
        ? await getTenantUserById(effectiveRegistryTenantId, userId)
        : await userService.loadUserDetails(userId);
      setUserData(data);
    } catch (error) {
      console.error('Failed to load user details', error);
    }
  };

  const handleAdminResetPassword = async () => {
    setResettingPassword(true);
    try {
      await userService.adminResetPassword(userId, effectiveRegistryTenantId);
      await loadUserDetails();
      setResetPasswordDialogOpen(false);
    } catch (error) {
      console.error('Admin password reset failed:', error);
    } finally {
      setResettingPassword(false);
    }
  };

  const handleActivateAccount = async () => {
    setActivatingAccount(true);
    try {
      await userService.adminActivateAccount(userId, effectiveRegistryTenantId);
      await loadUserDetails();
      setActivateAccountDialogOpen(false);
    } catch (error) {
      console.error('Account activation failed:', error);
    } finally {
      setActivatingAccount(false);
    }
  };

  const handleSaveAll = async () => {
    const updateUserDetailsDto = {
      name: userData.name,
      email: userData.email,
      role: roleAsString(userData.role),
      phoneNumber: userData.phoneNumber,
      jobPosition: userData.jobPosition,
      createdAt: formatDate(userData.createdAt),
    };

    setSaving(true);
    try {
      if (effectiveRegistryTenantId) {
        await updateTenantUserRecord(effectiveRegistryTenantId, {
          id: userId,
          name: userData.name,
          email: userData.email,
          role: roleAsString(userData.role),
          phoneNumber: userData.phoneNumber ?? '',
          jobPosition: userData.jobPosition ?? '',
          status: statusAsString(userData.status),
          tenantId: userData.tenantId || effectiveRegistryTenantId,
          tenantName: userData.tenantName ?? '',
          photoUrl: userData.photoUrl || userData.profilePicture || null,
          verifiedAt: userData.verifiedAt ?? null,
        });
      } else {
        await userService.updateUserDetails(userId, updateUserDetailsDto);
      }
      await loadUserDetails();
      toast.success('User details saved.');
    } catch (error) {
      console.error('Error updating user details:', error);
      const msg =
        error.response?.data?.error
        || error.response?.data?.message
        || error.message
        || 'Failed to save user details';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save user details');
    } finally {
      setSaving(false);
    }
  };

  // 👉 Show loading spinner if still loading
  if (isLoading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <ClockLoader size={56} />
      </Container>
    );
  }

  const openUserSessions = () => {
    const qs =
      effectiveRegistryTenantId != null
        ? `?registryTenantId=${encodeURIComponent(effectiveRegistryTenantId)}`
        : '';
    navigateTenant(`/sessions/${userId}${qs}`);
  };

  return (
    <Box sx={{ padding: 1, maxWidth: 800, margin: 'auto' }}>
      <Header title="User Details" />

      <Box
        elevation={3}
        sx={{
          padding: 1,
          maxWidth: 900,
          margin: 'auto',
          border: '1px solid',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {userData && (
          <Box>
            <Box
              display="flex"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              flexDirection={{ xs: 'column', sm: 'row' }}
              flexWrap="wrap"
              gap={2}
              mb={2}
            >
              <Box display="flex" alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
                  <Avatar
                    alt={userData.name || 'Profile Picture'}
                    src={userData.photoUrl || userData.profilePicture}
                    sx={{
                      width: { xs: 80, sm: 100 },
                      height: { xs: 80, sm: 100 },
                      border: userData.photoUrl || userData.profilePicture ? '2px solid #ccc' : '2px solid red',
                    }}
                  />
                  <Tooltip title="Choose image">
                    <IconButton
                      color="primary"
                      component="label"
                      sx={{ position: 'absolute', bottom: 0, right: 0, backgroundColor: '#657073' }}
                      onClick={() => setChangeImageDialogOpen(true)}
                    >
                      <PhotoCamera />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box ml={2} sx={{ minWidth: 0 }}>
                  <Typography variant="body1"><strong>Role:</strong> {userData.role}</Typography>
                  <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                    <strong>Email:</strong> {userData.email}
                  </Typography>
                </Box>
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                flexWrap="wrap"
                gap={1}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  flexShrink: 0,
                  alignSelf: { xs: 'stretch', sm: 'center' },
                  '& > .MuiButton-root': {
                    flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
                    minWidth: { xs: 0, sm: 'auto' },
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <Button variant="contained" color="primary" onClick={openUserSessions}>
                  View session
                </Button>
                {canActivateAccount ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setActivateAccountDialogOpen(true)}
                    disabled={activatingAccount}
                  >
                    Activate account
                  </Button>
                ) : null}
                {canResetPassword ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setResetPasswordDialogOpen(true)}
                    disabled={resettingPassword}
                  >
                    Reset password
                  </Button>
                ) : null}
              </Box>
            </Box>

            <Typography variant="h4" gutterBottom>General Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="User Name" variant="outlined" name="name" value={userData.name || ''} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Email" variant="outlined" name="email" value={userData.email || ''} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={6}>
                <SearchableSelect
                  name="role"
                  label="Role"
                  value={visibleRoleOptions.includes(roleAsString(userData.role)) ? roleAsString(userData.role) : ''}
                  onChange={(next) => handleChange({ target: { name: 'role', value: next } })}
                  options={visibleRoleOptions.map((role) => ({
                    value: role,
                    label: role === 'SYSTEMADMIN'
                      ? 'System admin'
                      : role === 'SERVICEADMIN'
                        ? 'Service admin'
                        : role.charAt(0) + role.slice(1).toLowerCase(),
                  }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Phone Number" variant="outlined" name="phoneNumber" value={userData.phoneNumber || ''} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Job Position"
                  variant="outlined"
                  name="jobPosition"
                  value={userData.jobPosition || ''}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Created At"
                    value={userData.createdAt ? dayjs(userData.createdAt) : null}
                    onChange={handleDateChange}
                    disableFuture
                    views={['day', 'month', 'year', 'hours', 'minutes']}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              color="secondary"
              sx={{ mt: 2 }}
              onClick={handleSaveAll}
              disabled={saving}
            >
              {saving ? <ClockLoader inline size={22} /> : 'Save All'}
            </Button>
          </Box>
        )}

        <EditPictureDialog
          open={changeImageDialogOpen}
          onClose={() => setChangeImageDialogOpen(false)}
          loadUserDetails={loadUserDetails}
          profileUserId={userId}
          registryTenantId={effectiveRegistryTenantId}
        />

        <Dialog
          open={resetPasswordDialogOpen}
          onClose={() => !resettingPassword && setResetPasswordDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reset password?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Reset the password for{' '}
              <strong>{userData?.name || 'this user'}</strong>
              {userData?.email ? (
                <>
                  {' '}
                  (<strong>{userData.email}</strong>)
                </>
              ) : null}
              ?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The password will be set to the default <strong>User@2000</strong>.
              The user must change it on their next login. Any active sessions
              for this account will be ended.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setResetPasswordDialogOpen(false)}
              disabled={resettingPassword}
            >
              Cancel
            </Button>
            <Button
              color="secondary"
              variant="contained"
              onClick={handleAdminResetPassword}
              disabled={resettingPassword}
            >
              {resettingPassword ? <ClockLoader inline size={20} /> : 'Reset'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={activateAccountDialogOpen}
          onClose={() => !activatingAccount && setActivateAccountDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Activate account?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Activate the account for{' '}
              <strong>{userData?.name || 'this user'}</strong>
              {userData?.email ? (
                <>
                  {' '}
                  (<strong>{userData.email}</strong>)
                </>
              ) : null}
              ?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The account will be set to <strong>Active</strong>, lockout flags
              will be cleared, and the password will be set to the default{' '}
              <strong>User@2000</strong>. The user must change it on their next
              login before using the application. Any active sessions will be
              ended.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setActivateAccountDialogOpen(false)}
              disabled={activatingAccount}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={handleActivateAccount}
              disabled={activatingAccount}
            >
              {activatingAccount ? <ClockLoader inline size={20} /> : 'Activate'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default UserDetails;
